/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Space,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';

import { CloseSIcon, DownloadIcon } from '@tonic-one/react-icons';

import dayjs from 'dayjs';
import i18n from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from 'src/components/IconButton';
import { ReportApi } from '@Apis';
import MenuWithLabel from '../../../components/Common/Menu';
import { useDisclosure } from '../../../components/Common/Tooltip/TextEllipsis/utils';
import { useUtcOffsetStore } from '../../../models/useUtcOffsetModel';
import {
  ConsoleType,
  EXPORT_REPORT_TYPE,
  GenerateStatus,
} from '../../../utils/enums';
import { reportPendo } from '../../../utils/setupPendo';
import DelModal from '../ActionModal/del';
import BottomPagination from '../BottomPagination';
import ReportTable from '../ReportTable';
import Searcher from '../Searcher';
import SortableHeader from '../SortableHeader';
import { SERVICE_NAME_ORDER_MAP } from '../../../utils/service';
import { ServiceNameEnum } from '../../../types';
import LoadingBtn from '@Components/Common/LoadingBtn';
import { GeneratedStatus } from './generatedStatus';
import { getUtcTime } from '../../../utils/time';

const GenerateReports = (props: { withEditPermission: boolean }) => {
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [sortKey, setSortKey] = useState('-createdAt');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasClassicReports, setHasClassicReports] = useState(false);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    pageSize: 10,
    reportFrom: ConsoleType.NewConsole,
    type: '', //oneTime or scheduled
    format: '',
    widget: '',
    template: '', // TODO template filter
    input: '',
    trigger: 0, // operation trigger
  });
  const [totalCount, setTotalCount] = useState(0);
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableLoading, setTableLoading] = useState(true);

  const allSelected = useMemo(() => {
    if (!Array.isArray(tableData) || !Array.isArray(selectedIds)) return false;
    if (tableData.length === 0) return false;
    const canSelectTable = tableData.filter(
      (item: any) => item.generatedStatus !== GenerateStatus.InProgress
    );
    if (canSelectTable.length === 0) return false;
    const ids: any[] = canSelectTable.map((item: any) => item.id);
    for (const id of ids) {
      if (!selectedIds.includes(id)) return false;
    }
    return true;
  }, [tableData, selectedIds]);
  // table selection
  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      // OK_InProgress
      const ids = Array.isArray(tableData)
        ? tableData
            .filter(
              (item: any) => item.generatedStatus !== GenerateStatus.InProgress
            )
            .map((item: any) => item.id)
        : [];
      setSelectedIds(ids);
    }
  }, [allSelected, tableData]);

  const toggleSelectItem = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) {
        setSelectedIds((ids) => {
          return ids.filter((i) => i !== id);
        });
      } else {
        setSelectedIds((ids) => {
          return [...ids, id];
        });
      }
    },
    [selectedIds]
  );

  //ReportTable columns
  const columns = useMemo(
    () => [
      {
        id: 'selection',
        Header: () => {
          return (
            <Flex height="100%" alignItems="center">
              <Checkbox checked={allSelected} onChange={toggleSelectAll} />
            </Flex>
          );
        },
        accessor: (row: any) => {
          const checked = selectedIds.includes(row.id);
          const disabled = row.generatedStatus === GenerateStatus.InProgress;
          return (
            <Flex height="100%" alignItems="center">
              <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={() => {
                  toggleSelectItem(row.id);
                }}
              />
            </Flex>
          );
        },
        width: '3%',
        minWidth: 48,
      },
      {
        id: 'name',
        Header: () => {
          return (
            <SortableHeader
              label={t('reportCommon.labels.name')}
              headerKey="name"
              currentSortKey={sortKey}
              changeSortKey={setSortKey}
            />
          );
        },
        accessor: 'name',
        width: '13%',
      },
      {
        Header: t('reportCommon.labels.description'),
        accessor: (row: any) => {
          if (row.type === 'riskAI') {
            const from = getUtcTime(
              row.timeRange.gte,
              '+00:00',
              t('common.momentDateFormat') + '(UTCZ)'
            );
            const to = getUtcTime(
              row.timeRange.lte,
              '+00:00',
              t('common.momentDateFormat') + '(UTCZ)'
            );
            return t('reportCommon.riskAI.desc', { from, to });
          }

          return row.description;
        },
        width: '13%',
      },
      {
        id: 'orgName',
        Header: t('reportCommon.labels.organization'),
        accessor: (row: any): string => {
          if (row?.isGroup) {
            return t('common.label.allOrganizations');
          }
          return row?.orgName;
        },
        width: '13%',
      },
      {
        Header: t('reportCommon.labels.services'),
        accessor: ({ servicesTypes }: { servicesTypes: ServiceNameEnum[] }) => {
          const strArr: string[] = [];
          if (Array.isArray(servicesTypes)) {
            servicesTypes
              .sort((s1, s2) => {
                return SERVICE_NAME_ORDER_MAP[s1] - SERVICE_NAME_ORDER_MAP[s2];
              })
              .forEach((value: string) => {
                strArr.push(t(`common.service.${value}`));
              });
            const delimiter = i18n.language === 'ja' ? '、' : ', ';
            return strArr.join(delimiter);
          }
          return ' - ';
        },
        width: '15%',
      },
      {
        Header: t('reportCommon.labels.reportType'),
        accessor: ({ type }: any) => {
          let typeLabel = '';
          EXPORT_REPORT_TYPE.map((item) => {
            if (type === item.key) {
              typeLabel = t(item.value);
            }
          });
          return typeLabel || '-';
        },
        width: '9%',
        minWidth: 143,
      },
      {
        id: 'startedAt',
        Header: () => {
          return (
            <SortableHeader
              label={t('reportCommon.labels.started')}
              headerKey="createdAt"
              currentSortKey={sortKey}
              changeSortKey={setSortKey}
            />
          );
        },
        //startedAt maybe better, but no data for a few seconds in startedAt field
        accessor: (row: any) => {
          return row.createdAt
            ? dayjs(row.createdAt)
                .utcOffset(offset)
                .format(t('common.momentDateFormat'))
            : ' - ';
        },
        width: '14%',
      },
      {
        Header: t('reportCommon.labels.format'),
        accessor: 'format',
        width: '5%',
        minWidth: 86,
      },
      {
        id: 'generatedStatus',
        Header: t('reportCommon.labels.status'),
        width: '10%',
        minWidth: 222,
        noToolTip: true,
        accessor: (row: any) => (
          <GeneratedStatus generatedStatus={row.generatedStatus} />
        ),
      },
      {
        id: 'action',
        noToolTip: true,
        Header: t('reportCommon.labels.download'),
        accessor: (row: any) => {
          if (row.generatedStatus !== GenerateStatus.Successful) {
            return <></>;
          }

          return (
            <LoadingBtn
              variant="ghost"
              onClick={async () => {
                if (row.type === 'riskAI') {
                  window.open(row.downloadLink);
                  return;
                }
                await handleDownload(row);
                reportPendo('Report.GeneratedReports.Download.Click', {
                  services: JSON.stringify(row.servicesTypes),
                  type: row.type,
                });
              }}
              border="0"
              icon={
                <Icon
                  as={DownloadIcon}
                  color={colorStyle.color.emphasis}
                  cursor="pointer"
                />
              }
            >
              <></>
            </LoadingBtn>
          );
        },
        width: '5%',
        minWidth: 93,
      },
    ],
    [allSelected, selectedIds, sortKey]
  );

  //page actions
  const changeCurrentPage = (page: any) => {
    setFilterParams({
      ...filterParams,
      page,
      trigger: new Date().valueOf(),
    });
  };
  const changePageSize = (pageSize: any) => {
    setFilterParams({
      ...filterParams,
      page: 1,
      pageSize,
      trigger: new Date().valueOf(),
    });
  };

  const handleDownload = async ({ id, name }: any) => {
    //get report download url
    try {
      const res = await ReportApi.downloadReport(id, filterParams.reportFrom);
      if (res) {
        const url = window.URL.createObjectURL(
          new Blob([res as any], { type: 'application/pdf' })
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = name + '-' + dayjs().utcOffset(offset).format('YYYYMMDD');
        a.click();
        a.remove();
      }
    } catch (error: any) {
      const code = error?.data?.status || 'Unknown';
      window.showNotification({
        titleMsg: t('reportCommon.alert.downloadReport.fail'),
        bodyMsg:
          [404004].indexOf(code) >= 0
            ? t(`reportCommon.alert.downloadReport.failDesc.${code}`)
            : t('reportCommon.alert.downloadReport.failDesc'),
        severity: 'error',
      });
    }
  };

  //get table data
  const fetchData = async () => {
    setTableLoading(true);
    const { reportFrom, type, input, page, pageSize, template } = filterParams;
    // judge customer has classic schedule report or not
    const queryClassicParams = {
      reportFrom: ConsoleType.ClassicConsole,
      offset: 0,
      limit: 1,
      type: '', //oneTime or scheduled
      keyword: '',
      template: '', // TODO template filter
      sort: 'name',
    };
    const classicData = (await ReportApi.getReports(queryClassicParams)).data;
    if (Array.isArray(classicData?.list) && classicData?.list.length > 0) {
      setHasClassicReports(true);
    } else {
      setHasClassicReports(false);
    }
    // query reports
    const params = {
      reportFrom,
      type,
      template, // TODO
      keyword: input,
      sort: sortKey,
      limit: pageSize,
      offset: pageSize * (page - 1),
    };
    const data = (await ReportApi.getReports(params)).data;
    // 转换manual scan report name
    if (Array.isArray(data?.list)) {
      data.list.forEach((item: any) => {
        if (item.name === 'Manual Scan Report') {
          item.name = t('reportCommon.manualScanMap.Name');
        }
        if (item.name === 'Risk AI Report') {
          item.name = t('reportCommon.riskAI.name');
        }
      });
      setTableData(data.list);
      setTotalCount(data.count);
      setSelectedIds([]);
    }
    setTableLoading(false);
  };

  // TODO: sortKey changed or trigger changed
  useEffect(() => {
    fetchData().then();
    //console.log('generate reports params: ' + JSON.stringify(filterParams));
    return () => {
      //clearTimeout(timeRef.current)
      //timeRef.current = null
    };
  }, [filterParams?.trigger, sortKey]);

  //delete action
  const resetDel = () => {
    setSelectedIds([]);
  };
  // modal
  const [delModalTitle, setDelModalTitle] = useState('Delete report');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [delModalContent, setDelModalContent] = useState('Delete report');
  const [delLoading, setDelLoading] = useState(false); // delete button loading
  const handleDel = () => {
    // single
    if (selectedIds.length === 1) {
      setDelModalTitle(t('reportCommon.actions.delReport.title'));
      tableData.map((item: any) => {
        if (item.id === selectedIds[0]) {
          setDelModalContent(
            t('reportCommon.actions.delReport.desc', { name: item.name })
          );
        }
      });
    } else {
      // multiple
      setDelModalTitle(t('reportCommon.actions.delReports.title'));
      setDelModalContent(
        t('reportCommon.actions.delReports.desc', {
          count: selectedIds.length,
        })
      );
    }
    onOpen();
  };

  const confirmDel = async () => {
    setDelLoading(true);
    try {
      const res = await ReportApi.delReports(
        filterParams.reportFrom,
        selectedIds
      );
      if (res.data?.affected) {
        //successNotification(t('reportCommon.alert.delReport.success'));
        window.showNotification({
          titleMsg: t('reportCommon.alert.delReport.success'),
          severity: 'success',
        });
        await fetchData();
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('reportCommon.alert.delReport.fail'),
        bodyMsg: t('reportCommon.alert.delReport.failDesc'),
        severity: 'error',
      });
    } finally {
      let scheduleCount = 0;
      selectedIds.map((id) => {
        const raw = tableData.find((item) => item.id === id);
        if (raw.type === 'scheduled') {
          scheduleCount += 1;
        }
      });
      reportPendo('Report.GeneratedReports.Delete.Click', {
        count: selectedIds.length,
        oneTime: selectedIds.length - scheduleCount,
        scheduled: scheduleCount,
      });
      onClose();
      setDelLoading(false);
    }
  };

  const reportTypeMenuItems = useMemo(() => {
    return [
      { key: '', value: t('reportCommon.enums.exportType.all') },
      ...EXPORT_REPORT_TYPE.map((item) => {
        return { key: item.key, value: t(item.value) };
      }),
    ];
  }, []);

  const reportTypeMenuItemsForClassicConsole = [
    {
      key: 'scheduled',
      value: t('reportCommon.enums.exportType.scheduled'),
    },
  ];

  const reportTypeMenuClick = (e: any) => {
    setFilterParams({
      ...filterParams,
      type: e.target.getAttribute('value'),
      page: 1,
      trigger: new Date().valueOf(),
    });
  };

  const reportFromMenuItems = [
    {
      key: ConsoleType.NewConsole,
      value: t('reportCommon.enums.reportFrom.newConsole'),
    },
    {
      key: ConsoleType.ClassicConsole,
      value: t('reportCommon.enums.reportFrom.classicConsole'),
    },
  ];

  const reportFromMenuClick = (e: any) => {
    if (e.target.getAttribute('value') === ConsoleType.ClassicConsole) {
      filterParams.type = 'scheduled';
    }
    setFilterParams({
      ...filterParams,
      reportFrom: e.target.getAttribute('value'),
      page: 1,
      trigger: new Date().valueOf(),
    });
  };

  return (
    <Box width="100%">
      {selectedIds.length > 0 ? (
        <Stack direction="row" margin="4x">
          <Button
            variant="secondary"
            mr="4x"
            onClick={handleDel}
            disabled={!props.withEditPermission}
          >
            {t('reportCommon.buttons.delete')}
          </Button>
          <IconButton onClick={resetDel}>
            <Icon as={CloseSIcon}></Icon>
          </IconButton>
          <Box lineHeight="2rem">
            <Text color={colorStyle.color.tertiary}>
              {selectedIds.length}&nbsp;
              {t('reportCommon.placeholders.selected')}
            </Text>
          </Box>
        </Stack>
      ) : (
        <Stack direction="row" margin="4x">
          {hasClassicReports && (
            <div>
              <MenuWithLabel
                minWidth={200}
                menuItems={reportFromMenuItems}
                selectedKey={filterParams?.reportFrom}
                onClick={reportFromMenuClick}
                label={t('reportCommon.labels.reportFrom')}
              />
              <Space width="2x" />
            </div>
          )}
          <MenuWithLabel
            minWidth={200}
            menuItems={
              filterParams.reportFrom === ConsoleType.NewConsole
                ? reportTypeMenuItems
                : reportTypeMenuItemsForClassicConsole
            }
            selectedKey={filterParams?.type}
            onClick={reportTypeMenuClick}
            label={t('reportCommon.labels.reportType')}
          />
          <Space width="2x" />
          <Searcher
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            placeholder={t('reportCommon.labels.name')}
          />
        </Stack>
      )}
      <ReportTable
        loading={tableLoading}
        columns={columns}
        data={tableData}
        tableStyle={{ width: '100%' }}
      />
      <BottomPagination
        totalCount={totalCount}
        currentPage={filterParams.page}
        pageSize={filterParams?.pageSize}
        changeCurrentPage={changeCurrentPage}
        changePageSize={changePageSize}
        iconWithTip={true}
        tip={t('reportCommon.autoDeleteTips')}
      />
      <DelModal
        visible={isOpen}
        onClose={onClose}
        onConfirm={confirmDel}
        loading={delLoading}
        title={delModalTitle}
        content={delModalContent}
        withEditPermission={props.withEditPermission}
      ></DelModal>
    </Box>
  );
};

export default GenerateReports;
